/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════
   BUILDER B · KEYBOARD AND SCREEN-READER FOCUS (accessibility audit, 2026-10-12).
   Audit and evidence: projects/pop-cards/output/2026-10-12-a11y-audit/REPORT.md (venture repo).

   Its own file, loaded AFTER builder-b.js and wrapping PP.builderB the way pp-cart-cards.js does, so it changes no
   behaviour of B's and no line of builder-b.js. It only decides WHERE THE FOCUS GOES when B would otherwise drop it
   on the floor (document.body), which a keyboard or screen-reader user experiences as "sent back to the top of the
   page":
     · a step change (ppbGo blurs whatever had the focus, so a phone keyboard goes away). A button that had it gets
       it back; a field that had it does not (that is the keyboard going away, on purpose); a control that is gone
       (the file input after the fourth photo, Next on Send) hands it to the current step's pill, or Send's heading.
       Nothing moves when nothing had the focus (a draft restored on load, a swipe).
     · Escape putting a selected picture down: to that picture on the card.
     · the two dialogs (Leave your card?, A card on the go): into the dialog when it opens, kept inside while it is
       open (they are aria-modal), and back to what opened it when it closes.
     · the occasion menu and the Layout / Lettering / Colour sheets: into them when they open, back when they close.
   ⛔ Every focus here is { preventScroll: true }, and none of it ever focuses a text field, so it can neither scroll
   the card nor raise a phone keyboard.
   ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  const PP = window.PP || {};
  if (typeof PP.builderB !== 'function') return;
  const inner = PP.builderB;

  const CHROME = '.ppb-head, .ppb-steps, .ppb-over, .ppb-tray, .ppb-bar, .ppb-page, .ppb-sheet, .ppb-sendpage, .ppb-occ, .ppb-dlg, .ppb-zoombtn';
  const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function shown(el) {
    if (!el || el === document.body || !el.isConnected) return false;
    if (el.checkVisibility) return el.checkVisibility({ visibilityProperty: true });
    return el.offsetParent !== null;
  }
  function isTyping(el) {
    return !!(el && el.matches && el.matches('textarea, select, [contenteditable="true"], input:not([type="button"]):not([type="submit"]):not([type="file"]):not([type="range"]):not([type="checkbox"]):not([type="radio"])'));
  }
  function lost() { const a = document.activeElement; return !a || a === document.body || !shown(a); }
  function put(el) { if (el) { try { el.focus({ preventScroll: true }); } catch (e) { /* best-effort */ } } }
  function firstShown(sel, root) { return Array.prototype.find.call((root || document).querySelectorAll(sel), shown) || null; }
  function curPill() { return firstShown('.ppb-steps .ppb-pill.is-cur'); }

  // Tab and Shift+Tab stay inside an open modal dialog.
  function trap(e, box) {
    const f = Array.prototype.filter.call(box.querySelectorAll(FOCUSABLE), shown);
    if (!f.length) return;
    const a = document.activeElement, first = f[0], last = f[f.length - 1];
    if (!box.contains(a)) { e.preventDefault(); put(first); }
    else if (e.shiftKey && a === first) { e.preventDefault(); put(last); }
    else if (!e.shiftKey && a === last) { e.preventDefault(); put(first); }
  }

  PP.builderB = function () {
    const c = inner.apply(this, arguments);

    const baseGo = c.ppbGo;
    c.ppbGo = function (key) {
      const had = document.activeElement;
      const out = baseGo.apply(this, arguments);
      if (!had || had === document.body || isTyping(had)) return out;
      const keep = had.closest && had.closest(CHROME) ? had : null;
      const self = this;
      setTimeout(function () {
        if (!lost() || self.stage !== 'builder') return;
        if (keep && shown(keep)) { put(keep); return; }
        if (self.ppbStep === 'send') {
          const h = document.getElementById('ppb-send-h');
          if (h && shown(h)) { if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1'); put(h); return; }
        }
        put(curPill());
      }, 80);
      return out;
    };

    /* On Send the card is under the page: its planes were six invisible Tab stops after Add to cart (and on a phone,
       with an address field focused, the only way on from the last field). Nothing on the card is for editing
       there, as on Preview, which panelInteractive() already excludes. */
    const basePI = c.panelInteractive;
    c.panelInteractive = function () {
      return basePI.apply(this, arguments) && this.ppbStep !== 'send';
    };

    const baseLeave = c.leaveToFullCard;
    c.leaveToFullCard = function () {
      const i = this.selectedPhoto;
      const out = baseLeave.apply(this, arguments);
      if (i !== null && i !== undefined && this.selectedPhoto === null) {
        setTimeout(function () {
          if (!lost()) return;
          const thumbs = document.querySelectorAll('.ppb-thumb');
          if (thumbs[i] && shown(thumbs[i])) { put(thumbs[i]); return; }
          const planes = Array.prototype.filter.call(document.querySelectorAll('.pp-plane--panel.is-editable'), shown);
          put(planes[i] || curPill());
        }, 60);
      }
      return out;
    };

    const baseInit = c.init;
    c.init = function () {
      const out = baseInit.apply(this, arguments);
      const self = this;
      if (typeof this.$watch !== 'function') return out;
      let opener = null;
      const into = function (on, boxSel, firstSel) {
        if (on) {
          opener = document.activeElement;
          // A sheet can take a few frames to show (measured up to ~200 ms on the Layout sheet): try for 0.6 s.
          let tries = 0;
          const go = function () {
            const box = firstShown(boxSel);
            if (!box) { if (++tries < 12) setTimeout(go, 50); return; }
            put(firstShown(firstSel, box) || firstShown(FOCUSABLE, box));
          };
          self.$nextTick(function () { setTimeout(go, 30); });
        } else {
          const back = opener; opener = null;
          setTimeout(function () { if (lost()) put(back && shown(back) ? back : curPill()); }, 60);
        }
      };
      this.$watch('leaveOpen', function (on) { into(on, '.ppb-dlg .ppb-dlg__box', '.ppb-cta'); });
      this.$watch('switchAsk', function (on) { into(on, '.ppb-dlg .ppb-dlg__box', '.ppb-cta'); });
      this.$watch('ppbOccOpen', function (on) { into(on, '.ppb-occ__box', '.ppb-occ__opt.is-on'); });
      this.$watch('ppbSheet', function (v, was) { if (!!v !== !!was) into(!!v, '.ppb-sheet', '[aria-checked="true"]'); });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !(self.leaveOpen || self.switchAsk)) return;
        const box = firstShown('.ppb-dlg .ppb-dlg__box');
        if (box) trap(e, box);
      }, true);
      return out;
    };

    return c;
  };
})();
